import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PackageSearch, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import type { OrderRow } from '@/Redux/Slices/operationsApi';
import {
  DELIVERY_AGGREGATORS_PERMISSIONS,
  DELIVERY_AGGREGATORS_ROUTES,
  invalidateAggregatorOrders,
  useAggregatorOrderQuery,
  type AggregatorOrderLink,
  type AggregatorOrderStatus,
} from './deliveryAggregatorsApi';
import { AGGREGATOR_TRANSITIONS, apiMessage, apiStatus, formatDateTime, toneForAggregatorStatus } from './lib';

/**
 * One aggregator order, opened from its row in the incoming queue.
 *
 * The gate lives here: Accept confirms the order, fires the kitchen ticket
 * and records the gross payment in one server call — nothing upstream of it
 * has done any of that yet, regardless of the venue's own
 * `requireWaiterConfirmation` setting (ADR-0028 §B2). Reject and Cancel both
 * ask for a reason through the same confirm surface every other gate in this
 * app uses (`Pages/Platform/Approvals.tsx`), so a reason is captured before
 * it round-trips to the provider's own app as an acknowledgement.
 */
export default function OrderDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();
  const { confirm, prompt } = useConfirm();

  const { data: link, isFetching, isError, error, refetch } = useAggregatorOrderQuery(id);
  const reload = () => {
    refetch();
    dispatch(invalidateAggregatorOrders(id));
  };

  const canActOn = (targets: AggregatorOrderStatus[]) =>
    Boolean(link && targets.some((to) => AGGREGATOR_TRANSITIONS[link.status]?.includes(to)));

  const act = async (request: () => Promise<unknown>, successKey: string) => {
    try {
      await request();
      toast.success(t(successKey));
      dispatch(invalidateAggregatorOrders(id));
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('deliveryAggregators.errors.actionFailed')));
      // 400 = an illegal transition (someone else just acted on this row), 404
      // = it is gone. Both are states the row can only be read back out of —
      // never leave the operator staring at buttons for a status that no
      // longer applies, and never pretend the click worked.
      const status = apiStatus(requestError);
      if (status === 400 || status === 404 || status === 409) reload();
    }
  };

  const onAccept = async () => {
    if (!link) return;
    const ok = await confirm({
      title: t('deliveryAggregators.detail.acceptTitle', { id: link.externalOrderId }),
      body: t('deliveryAggregators.detail.acceptBody'),
      confirmLabel: t('deliveryAggregators.detail.accept'),
    });
    if (!ok) return;
    await act(() => api.post(DELIVERY_AGGREGATORS_ROUTES.accept(link.id)), 'deliveryAggregators.detail.accepted');
  };

  const onReject = async () => {
    if (!link) return;
    const reason = await prompt({
      title: t('deliveryAggregators.detail.rejectTitle', { id: link.externalOrderId }),
      body: t('deliveryAggregators.detail.rejectBody'),
      label: t('deliveryAggregators.detail.reasonLabel'),
      placeholder: t('deliveryAggregators.detail.reasonPlaceholder'),
      confirmLabel: t('deliveryAggregators.detail.reject'),
      tone: 'danger',
    });
    if (reason === null) return;
    await act(
      () => api.post(DELIVERY_AGGREGATORS_ROUTES.reject(link.id), { reason: reason.trim() || undefined }),
      'deliveryAggregators.detail.rejected',
    );
  };

  const onCancel = async () => {
    if (!link) return;
    const reason = await prompt({
      title: t('deliveryAggregators.detail.cancelTitle', { id: link.externalOrderId }),
      body: t('deliveryAggregators.detail.cancelBody'),
      label: t('deliveryAggregators.detail.reasonLabel'),
      placeholder: t('deliveryAggregators.detail.reasonPlaceholder'),
      confirmLabel: t('deliveryAggregators.detail.cancel'),
      tone: 'danger',
    });
    if (reason === null) return;
    await act(
      () => api.post(DELIVERY_AGGREGATORS_ROUTES.cancel(link.id), { reason: reason.trim() || undefined }),
      'deliveryAggregators.detail.cancelled',
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={link ? t('deliveryAggregators.detail.title', { id: link.externalOrderId }) : t('deliveryAggregators.detail.titleGeneric')}
      description={link?.providerKey}
      className="max-w-2xl"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {link && can(DELIVERY_AGGREGATORS_PERMISSIONS.cancel) && canActOn(['CANCELLED']) && (
            <Button variant="danger" size="lg" onClick={() => void onCancel()}>
              {t('deliveryAggregators.detail.cancel')}
            </Button>
          )}
          {link && can(DELIVERY_AGGREGATORS_PERMISSIONS.acceptReject) && canActOn(['REJECTED']) && (
            <Button variant="danger" size="lg" onClick={() => void onReject()}>
              {t('deliveryAggregators.detail.reject')}
            </Button>
          )}
          {link && can(DELIVERY_AGGREGATORS_PERMISSIONS.acceptReject) && canActOn(['ACCEPTED']) && (
            <Button size="lg" onClick={() => void onAccept()}>
              {t('deliveryAggregators.detail.accept')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} className="ms-auto">
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {isFetching && !link && <LoadingState />}
      {isError && !link && (
        <ErrorState message={apiMessage(error, t('deliveryAggregators.states.loadFailed'))} onRetry={reload} />
      )}
      {link && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForAggregatorStatus(link.status)}>
              {t(`deliveryAggregators.status.${link.status}`)}
            </Badge>
            <span className="text-sm text-ink-muted">
              {t('deliveryAggregators.detail.received', { at: formatDateTime(link.createdAt, i18n.language) })}
            </span>
          </div>

          <MoneySummary link={link} />

          {link.courierName && (
            <p className="text-sm text-ink-muted">
              {t('deliveryAggregators.detail.courier', {
                name: link.courierName,
                phone: link.courierPhone ?? t('deliveryAggregators.detail.noPhone'),
              })}
            </p>
          )}

          {link.order && <OrderItems orderId={link.order.id} orderNumber={link.order.number} />}
        </div>
      )}
    </Dialog>
  );
}

function MoneySummary({ link }: { link: AggregatorOrderLink }) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  return (
    <dl className="grid grid-cols-3 gap-3 rounded-lg border p-3 text-sm">
      <div>
        <dt className="text-ink-muted">{t('deliveryAggregators.detail.gross')}</dt>
        <dd className="tabular-nums font-medium">{format(link.grossAmount)}</dd>
      </div>
      <div>
        <dt className="text-ink-muted">{t('deliveryAggregators.detail.commission')}</dt>
        <dd className="tabular-nums font-medium">{format(link.commissionAmount)}</dd>
      </div>
      <div>
        <dt className="text-ink-muted">{t('deliveryAggregators.detail.net')}</dt>
        <dd className="tabular-nums font-medium">{format(link.netPayoutAmount)}</dd>
      </div>
    </dl>
  );
}

/**
 * The order this link produced — fetched separately, on purpose.
 *
 * `AggregatorOrderLink.order` (this module's own `include`) never carries
 * line items; Prisma does not walk a relation's own relations without a
 * second, nested `include`, and this module's controller does not ask for
 * one. So the line items a kitchen would cook come from the core order API
 * (`GET /orders/:id`, `orders.view`) — the same route every other order
 * screen in this app reads a single order from.
 *
 * This is also where ADR-0028's own honest gap becomes a real UI state: a
 * real marketplace's webhook (Uber Eats' own, per the connector's doc
 * comment) is a thin "something changed, go fetch it" pointer, not the order
 * itself — this module ingests only what its own inbound listener recognises
 * as a full `order.created` body today, so a hypothetically-partial order
 * would surface here as a linked order with a `RECEIVED` status and no
 * resolvable items, not as an ordinary order that merely has none. Rendering
 * that as a quiet, empty table would look like a mistake — a ticket for
 * nothing — so it gets its own callout instead of `EmptyState`.
 */
function OrderItems({ orderId, orderNumber }: { orderId: string; orderNumber: string }) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const { data, loading, error, reload } = useApi<OrderRow>(`/orders/${orderId}`);

  return (
    <section className="space-y-2 border-t pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {t('deliveryAggregators.detail.itemsTitle', { number: orderNumber })}
        </h3>
        {data && (
          <span className="tabular-nums text-sm font-medium">{format(data.total)}</span>
        )}
      </div>

      {loading && <LoadingState label={t('common.loading')} />}

      {!loading && error && (
        <div
          role="alert"
          className="rounded-lg border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30"
        >
          <div className="flex items-start gap-2">
            <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
            <div className="space-y-1">
              <p className="font-medium">{t('deliveryAggregators.detail.itemsLoadFailed')}</p>
              <p className="text-ink-muted">{error}</p>
              <Button variant="outline" size="sm" onClick={reload}>
                {t('common.tryAgain')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && data && data.items.length === 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          <div className="flex items-start gap-2">
            <Icon as={PackageSearch} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="space-y-1">
              <p className="font-medium">{t('deliveryAggregators.detail.itemsNotYetKnownTitle')}</p>
              <p className="text-ink-muted">{t('deliveryAggregators.detail.itemsNotYetKnownHint')}</p>
              <Button variant="outline" size="sm" onClick={reload}>
                {t('common.tryAgain')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('deliveryAggregators.detail.item')}</TH>
                <TH>{t('deliveryAggregators.detail.quantity')}</TH>
                <TH>{t('deliveryAggregators.detail.lineTotal')}</TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((item) => (
                <TR key={item.id}>
                  <TD>
                    <p className="font-medium">{item.name}</p>
                    {item.notes && <p className="text-xs text-ink-muted">{item.notes}</p>}
                  </TD>
                  <TD className="tabular-nums">{item.quantity}</TD>
                  <TD className="tabular-nums">{format(item.total)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </section>
  );
}
